import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KAMUS_ID } from './id';

export type Bahasa = 'en' | 'id';

const KUNCI = 'lensa.bahasa';

/**
 * Dua bahasa untuk teks pemakai (§H9).
 *
 * **Kuncinya kalimat Inggrisnya sendiri**, bukan `nav.dashboard` atau
 * sejenisnya. Tiga alasan, dan ketiganya keluar dari bentuk projek ini:
 *
 * 1. Berkas sumber tetap bisa dibaca tanpa membuka kamus. `t('Sign out')`
 *    mengatakan apa yang akan muncul di layar; `t('menu.akun.keluar')` tidak.
 * 2. Bahasa bawaannya **tidak bisa hilang**. Kunci yang belum diterjemahkan
 *    jatuh ke dirinya sendiri, jadi kalimat baru yang lupa dimasukkan ke kamus
 *    muncul dalam bahasa Inggris — bukan sebagai `menu.akun.keluar` di tengah
 *    layar, yang persis kegagalan yang paling sering terlihat di aplikasi
 *    ber-i18n.
 * 3. Menerjemahkan bisa dikerjakan bertahap tanpa satu pun halaman rusak.
 *
 * Ongkosnya: dua kalimat Inggris yang sama persis tapi berbeda arti akan
 * berbagi satu terjemahan. Di aplikasi ini belum ada kasusnya; kalau nanti ada,
 * bedakan dengan menambahkan konteks di dalam kalimatnya sendiri, bukan dengan
 * pindah ke kunci abstrak.
 *
 * **Isi log tidak ikut diterjemahkan** — keputusan 23 Agustus 2026. Pesan
 * kejadian kamera dan `stderr` ffmpeg datang dari server dan dari ffmpeg
 * sendiri; menerjemahkannya berarti menerjemahkan pesan yang akan dicari orang
 * di internet apa adanya. Yang diterjemahkan di halaman Logs cuma kerangkanya:
 * judul kolom, filter, tombol.
 */
interface Isi {
  bahasa: Bahasa;
  setBahasa: (b: Bahasa) => void;
  t: (teks: string, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<Isi | null>(null);

function bacaTersimpan(): Bahasa {
  try {
    return localStorage.getItem(KUNCI) === 'id' ? 'id' : 'en';
  } catch {
    // Mode privat / penyimpanan diblokir.
    return 'en';
  }
}

/** Ganti `{nama}` dengan nilainya. Placeholder yang tidak diisi dibiarkan utuh. */
function isi(teks: string, vars?: Record<string, string | number>): string {
  if (!vars) return teks;
  return teks.replace(/\{(\w+)\}/g, (cocok, nama: string) =>
    nama in vars ? String(vars[nama]) : cocok,
  );
}

export function BahasaProvider({ children }: { children: React.ReactNode }) {
  const [bahasa, setBahasaState] = useState<Bahasa>(bacaTersimpan);

  useEffect(() => {
    try {
      localStorage.setItem(KUNCI, bahasa);
    } catch {
      /* penyimpanan diblokir — pilihan tetap berlaku untuk sesi ini */
    }
    // `lang` pada <html> bukan hiasan: pembaca layar memilih suara dari sini,
    // dan tanpa itu kalimat Indonesia dibacakan dengan fonem Inggris.
    document.documentElement.lang = bahasa;
  }, [bahasa]);

  const t = useCallback(
    (teks: string, vars?: Record<string, string | number>) =>
      isi(bahasa === 'id' ? (KAMUS_ID[teks] ?? teks) : teks, vars),
    [bahasa],
  );

  const nilai = useMemo<Isi>(
    () => ({ bahasa, setBahasa: setBahasaState, t }),
    [bahasa, t],
  );

  return <Ctx.Provider value={nilai}>{children}</Ctx.Provider>;
}

export function useBahasa(): Isi {
  const isi = useContext(Ctx);
  if (!isi) throw new Error('useBahasa dipakai di luar BahasaProvider');
  return isi;
}
